/**
 * EmbeddedEntityUI
 *
 * Renderer for `UIElementType.EmbeddedEntity`. Links one record of a target
 * entity and renders that record's full sidebar (`<Entity/>`) inline and
 * editable — New Record / Subscribe / Locked controls, name / status / groups,
 * and the record's default field layout — beneath a record picker.
 *
 * Selection (which record is linked) lives in the HOST form's Valtio state under
 * `entityReferences[entityGroupId || entityId]`: read via `useEntityRefScope`
 * (resolved against the host, before the inner scope) and written/cleared via
 * `useSaveEntityReference`. The picker follows the `ReferenceSelect` pattern —
 * search is delegated to the registry's Algolia-backed `setSearchQuery` rather
 * than filtering the local cache, so any record (not just cached ones) is
 * selectable. The picked record is mounted in an `EntityRecordScope` so
 * `<Entity/>` and its fields persist to `entities/{entityId}/records/{recordId}`
 * through the standard pipeline. With no record selected, `<Entity/>` renders its
 * layout disabled (`showWhenEmpty`) so the empty form is visible and a record can
 * be created via its own New Record control. While searching, a
 * `CREATE_RECORD_OPTION_VALUE` sentinel option also creates a record named with
 * the typed query and selects it inline, mirroring `ReferenceSelect`.
 *
 * Protection (`FormType.protected`) and `triggerSearchSync` configured on the
 * element in the layout editor are honored here, matching `EntityUI`.
 *
 * The undo stack stays the host form's: `FormPageContent` mounts the one
 * `useFormUndoKeybindings` listener and owns the chord and the buttons, and a second
 * listener here would take one press as two steps. This element only ends the entries
 * its picker invalidates — see `EmbeddedEntityContent` (SYD-2447).
 *
 * Width: the element shrinks to its Row (`min-width: 0`). The bordered record
 * box is `width: 100%` with `box-sizing: border-box`, so its padding and border
 * stay inside that width rather than adding to it, and it resets `--form-content-max-width` to `100%`. That property is the
 * page's content width, so every cap `<Entity/>`'s layout reads from it would
 * otherwise let a nested Row run as wide as the page from inside an indented,
 * padded box — past the viewport on a phone. Inside the box, the caps resolve
 * against the box instead.
 *
 * Renders only at a form's top level (`insideForm && !insideEntity`). In a
 * non-form context (e.g. the Records page) or nested inside another entity
 * record, the selection write (always the host form version) and the read
 * diverge, so the element shows a short notice and warns rather than rendering
 * a control whose selection can't round-trip. This `insideEntity` guard also
 * enforces the one-level rule (a record in a form, never a record in a record):
 * any nested embed sits inside the parent's `EntityRecordScope`, so it short-
 * circuits here and never recurses.
 */
import { CSSProperties, FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Select, Typography, theme } from 'antd';

import { UIElement, UIEmbeddedEntity } from '#types/System.types';
import { EntityRecordScope, useEntityRecordScope, entityRecordActions } from '../../store';
import { formUndoStack } from '@/store/form/formUndoStack';
import { useEntityRefScope } from '../../use/useEntityRefScope';
import { useSaveEntityReference } from '../../use/useSaveEntityReference';
import { useProtectedFields } from '../../use/useProtectedFields';
import { useEntityRecordRegistry } from '@/store/entityRecordsRegistry';
import { UserContext } from '../../providers/UserContext';
import { MessageContext } from '../../providers/MessageContext';
import { CREATE_RECORD_OPTION_VALUE, CreateRecordOptionLabel } from '../createRecordOption';
import FieldLabel from '../atoms/FieldLabel';
import FlexCol from '../atoms/FlexCol';
import { FORM_PAGE_CSS_VARS } from '../atoms/FORM_PAGE_CSS_VARS';
import Entity from '../Entity';

const { Text } = Typography;

type RecordBoxStyle = CSSProperties &
  Partial<Record<'--form-content-max-width', string>>;

const SHRINKABLE = { minWidth: 0 } as const;

interface EmbeddedEntityContentProps {
  entityId: string;
  entityGroupId?: string;
  required?: boolean;
  disabled: boolean;
  triggerSearchSync?: boolean;
  selectedRecordId: string | undefined;
  /** Denormalized record name from the host reference; covers the gap before the live record loads so the picker label never flashes "Unnamed Record". */
  referenceName?: string;
}

const EmbeddedEntityContent: FC<EmbeddedEntityContentProps> = ({
  entityId,
  entityGroupId,
  required,
  disabled,
  triggerSearchSync,
  selectedRecordId,
  referenceName,
}) => {
  const { token } = theme.useToken();
  const { user } = useContext(UserContext);
  const { handleFirestoreWriteError } = useContext(MessageContext);
  const scope = useEntityRecordScope();
  const entity = scope.entity.data;
  const selectedRecordName = scope.entityRecord.data?.name;
  const saveEntityReference = useSaveEntityReference();
  const { entityRecords, loading, error, searchQuery, setSearchQuery } =
    useEntityRecordRegistry({
      entityId,
      entityGroupId,
    });

  /**
   * The stack belongs to `FormPageContent`, bounded by the form version path — which
   * does not move when the picker does. Entries recorded against the record leaving
   * the screen would outlive it: the buttons keep naming a delete made on the previous
   * record, and the press is spent on the one now in front of the user. So the picker's
   * record ends them, the way `useFormUndoKeybindings` ends a surface's entries on the
   * document that surface edits.
   *
   * The departing record's path is the scope, so only its own entries end. The host
   * form's edits and its ArTable deletes are recorded against the form version path
   * and stay where they are — reachable from the buttons for as long as the trash
   * holds them.
   *
   * What clears is a record leaving the picker, not the element arriving: only a
   * record that was on screen can have entries recorded against it. So the mount
   * clears nothing, and neither does the first record being picked — a visibility rule
   * can reveal this element mid-session, and with the picker empty `<Entity/>` renders
   * disabled and registers no undo target, so in both cases everything on the stack is
   * the host form's own and still revertible. Clearing the picker does clear, for the
   * same reason switching records does.
   */
  const recordPath = selectedRecordId
    ? `entities/${entityId}/records/${selectedRecordId}`
    : undefined;
  const shownRecordPath = useRef(recordPath);
  useEffect(() => {
    const left = shownRecordPath.current;
    if (left === recordPath) return;
    shownRecordPath.current = recordPath;
    if (left !== undefined) formUndoStack.clear(left);
  }, [recordPath]);

  const options = useMemo(() => {
    const base = entityRecords
      .filter((record) => record.docId)
      .map((record) => ({
        value: record.docId as string,
        label: record.name || 'Unnamed Record',
      }));
    const query = searchQuery.trim();
    if (!query) return base;
    return [...base, { value: CREATE_RECORD_OPTION_VALUE, label: `Create New Record "${query}"` }];
  }, [entityRecords, searchQuery]);

  const entityLabel = entityGroupId
    ? (entity?.recordGroups?.[entityGroupId]?.description?.shortLabel ??
      entityGroupId)
    : (entity?.description?.shortLabel ?? entityId);

  const handleChange = useCallback(
    async (value?: string) => {
      if (value === CREATE_RECORD_OPTION_VALUE) {
        const name = searchQuery.trim();
        if (!name) return;
        try {
          const createdRecordId = await entityRecordActions.createEntityRecord(
            entityId,
            user?.uid,
            entityGroupId,
            name,
          );
          setSearchQuery('');
          await saveEntityReference(
            createdRecordId,
            entityGroupId,
            { docId: createdRecordId, name },
            triggerSearchSync,
          );
        } catch (error) {
          handleFirestoreWriteError(error, '[EmbeddedEntity] createEntityRecord');
        }
        return;
      }

      const record = value
        ? entityRecords.find((r) => r.docId === value)
        : undefined;
      void saveEntityReference(
        value ?? undefined,
        entityGroupId,
        record,
        triggerSearchSync,
      );
    },
    [
      saveEntityReference,
      entityId,
      entityGroupId,
      triggerSearchSync,
      entityRecords,
      searchQuery,
      setSearchQuery,
      user,
      handleFirestoreWriteError,
    ],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) setSearchQuery('');
    },
    [setSearchQuery],
  );

  const recordBoxStyle: RecordBoxStyle = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadius,
    padding: token.paddingSM,
    backgroundColor: token.colorFillAlter,
    [FORM_PAGE_CSS_VARS.formContentMaxWidth]: '100%',
  };

  return (
    <FlexCol noGap style={SHRINKABLE}>
      <FieldLabel label={entityLabel} required={required} />
      <FlexCol style={recordBoxStyle} noGap>
        <Select
          value={selectedRecordId ?? undefined}
          onChange={handleChange}
          onOpenChange={handleOpenChange}
          options={options}
          loading={loading}
          showSearch={{
            filterOption: false,
            searchValue: searchQuery,
            onSearch: setSearchQuery,
          }}
          allowClear
          disabled={disabled}
          placeholder={`Select ${entityLabel}`}
          aria-label={`Select embedded ${entityLabel} record`}
          style={{ width: '100%' }}
          labelInValue={false}
          notFoundContent={
            loading
              ? 'Loading...'
              : error
                ? `Error: ${error instanceof Error ? error.message : String(error)}`
                : 'No results found'
          }
          labelRender={() =>
            selectedRecordId
              ? selectedRecordName || referenceName || 'Unnamed Record'
              : undefined
          }
          optionRender={(option) =>
            option.value === CREATE_RECORD_OPTION_VALUE ? (
              <CreateRecordOptionLabel label={String(option.label)} />
            ) : (
              option.label
            )
          }
        />
        <Entity
          showUnmappedFields={false}
          showWhenEmpty
          hostLocked={disabled}
        />
      </FlexCol>
    </FlexCol>
  );
};

interface EmbeddedEntityUIProps {
  uiElement: UIElement;
  disabled?: boolean;
}

const EmbeddedEntityUI: FC<EmbeddedEntityUIProps> = ({
  uiElement,
  disabled = false,
}) => {
  const element = uiElement as UIEmbeddedEntity;
  const { entityId, entityGroupId } = element;
  const referenceKey = entityGroupId || entityId || '';
  const { externalReference, insideForm, insideEntity } =
    useEntityRefScope(referenceKey);
  const { isEntityReferenceProtected } = useProtectedFields();
  const selectedRecordId = externalReference?.entityRecordId || undefined;

  if (!entityId) return null;

  if (!insideForm || insideEntity) {
    console.warn(
      `EmbeddedEntity "${entityId}" renders only at a form's top level; skipping here.`,
    );
    return (
      <Text type="secondary">
        Embedded records are only available directly within a form.
      </Text>
    );
  }

  const isProtected = isEntityReferenceProtected(
    `${entityId}:${entityGroupId || ''}`,
  );
  const effectiveDisabled = disabled || isProtected;

  return (
    <EntityRecordScope
      entityId={entityId}
      recordId={selectedRecordId}
      entityGroupId={entityGroupId}
      locked={effectiveDisabled}
    >
      <EmbeddedEntityContent
        entityId={entityId}
        entityGroupId={entityGroupId}
        required={element.required}
        disabled={effectiveDisabled}
        triggerSearchSync={element.triggerSearchSync}
        selectedRecordId={selectedRecordId}
        referenceName={externalReference?.entityRecordName}
      />
    </EntityRecordScope>
  );
};

export default EmbeddedEntityUI;
